import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, AlertCircle, CreditCard, DollarSign, Hash } from 'lucide-react';
import { PaymentError, getErrorDetails } from './nmi';
import { formatCurrency } from './utils';
import { CleanupManager, cleanupDOMElement } from '@/lib/utils/cleanup';

interface TransactionSuccessData {
  transactionid: string;
  amount: number;
  authcode?: string;
  cardLast4?: string;
  additionalMessage?: string;
  onClose?: () => void;
}

interface CardOnFileTransactionData {
  transactionid: string;
  amount: number;
  status: 'approved' | 'declined';
  authcode?: string;
  cardLast4?: string;
  responsetext?: string;
  additionalMessage?: string;
  onClose?: () => void;
}

// Singleton manager for notifications
class NotificationManager {
  private static instance: NotificationManager;
  private activeNotifications = new Map<string, { root: Root; element: HTMLElement; cleanup: CleanupManager }>();

  private constructor() {}

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  showNotification(
    id: string,
    Component: React.FC<{ onClose: () => void }>,
    onCloseCallback?: () => void
  ): void {
    // Clean up any existing notification with the same ID
    this.closeNotification(id);

    const element = document.createElement('div');
    element.style.position = 'fixed';
    element.style.zIndex = '9999';
    element.style.inset = '0';
    document.body.appendChild(element);

    const root = createRoot(element);
    const cleanup = new CleanupManager();

    // Register cleanup functions
    cleanup.register(() => {
      document.body.style.overflow = '';
      root.unmount();
      cleanupDOMElement(element);
    });

    const handleClose = () => {
      this.closeNotification(id);
      if (onCloseCallback) {
        onCloseCallback();
      }
    };

    // Store the notification
    this.activeNotifications.set(id, { root, element, cleanup });

    // Set body overflow
    document.body.style.overflow = 'hidden';

    // Render the component
    root.render(<Component onClose={handleClose} />);
  }

  closeNotification(id: string): void {
    const notification = this.activeNotifications.get(id);
    if (notification) {
      // Ensure body overflow is reset immediately
      document.body.style.overflow = '';
      
      // Clean up any lingering Radix UI Dialog overlays
      const radixOverlays = document.querySelectorAll('[data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]');
      radixOverlays.forEach(overlay => overlay.remove());
      
      // Clean up any Radix portals
      const radixPortals = document.querySelectorAll('[data-radix-portal]');
      radixPortals.forEach(portal => {
        if (portal.children.length === 0 || portal.querySelector('[data-state="closed"]')) {
          portal.remove();
        }
      });
      
      notification.cleanup.cleanup();
      this.activeNotifications.delete(id);
    }
  }

  closeAllNotifications(): void {
    this.activeNotifications.forEach((_, id) => {
      this.closeNotification(id);
    });
  }
}

const notificationManager = NotificationManager.getInstance();

// Global safety mechanism to reset body overflow on page visibility change
if (typeof window !== 'undefined') {
  // Reset on page load
  window.addEventListener('load', () => {
    document.body.style.overflow = '';
  });
  
  // Reset when page becomes visible (e.g., after navigation back)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      document.body.style.overflow = '';
    }
  });
  
  // Reset before page unload
  window.addEventListener('beforeunload', () => {
    document.body.style.overflow = '';
  });
}

export const showTransactionSuccess = ({ transactionid, amount, authcode, cardLast4, additionalMessage, onClose }: TransactionSuccessData) => {
  const ToastComponent: React.FC<{ onClose: () => void }> = ({ onClose: handleClose }) => {
    const [open, setOpen] = React.useState(true);

    React.useEffect(() => {
      return () => {
        document.body.style.overflow = '';
      };
    }, []);

    const handleCloseDialog = () => {
      setOpen(false);
      // Immediately reset body overflow to prevent blur issues
      document.body.style.overflow = '';
      setTimeout(() => {
        handleClose();
      }, 200);
    };

    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) {
          document.body.style.overflow = '';
        }
        setOpen(isOpen);
      }}>
        <AlertDialogContent className="max-w-md" overlayClassName="bg-black/70 backdrop-blur-sm">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="rounded-full bg-green-100 p-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <AlertDialogTitle className="text-xl mb-0">Payment Successful!</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Amount Charged
                    </span>
                    <span className="font-semibold text-lg">{formatCurrency(amount)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 flex items-center gap-2">
                      <Hash className="h-4 w-4" />
                      Transaction ID
                    </span>
                    <span className="font-mono text-sm">{transactionid}</span>
                  </div>
                  
                  {authcode && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Authorization Code</span>
                      <span className="font-mono text-sm">{authcode}</span>
                    </div>
                  )}
                  
                  {cardLast4 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Payment Method
                      </span>
                      <span className="text-sm">Card ending in {cardLast4}</span>
                    </div>
                  )}
                </div>
                
                {additionalMessage && (
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                    {additionalMessage}
                  </p>
                )}
                
                <p className="text-sm text-gray-500">
                  A receipt has been sent to your email address.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={handleCloseDialog} className="w-full">
              Done
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  notificationManager.showNotification('transaction-success', ToastComponent, onClose);
};

export const showTransactionError = (error: PaymentError | Error) => {
  // Handle both PaymentError and regular Error objects
  const paymentError = error instanceof PaymentError ? error : new PaymentError(error.message, 'UNKNOWN_ERROR');
  const errorDetails = getErrorDetails(paymentError);
  
  const ToastComponent: React.FC<{ onClose: () => void }> = ({ onClose: handleClose }) => {
    const [open, setOpen] = React.useState(true);

    React.useEffect(() => {
      return () => {
        document.body.style.overflow = '';
      };
    }, []);

    const handleCloseDialog = () => {
      setOpen(false);
      // Immediately reset body overflow to prevent blur issues
      document.body.style.overflow = '';
      setTimeout(() => {
        handleClose();
      }, 200);
    };

    return (
        <AlertDialog open={open} onOpenChange={(isOpen) => {
          if (!isOpen) {
            document.body.style.overflow = '';
          }
          setOpen(isOpen);
        }}>
          <AlertDialogContent className="max-w-md" overlayClassName="bg-black/70 backdrop-blur-sm">
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="rounded-full bg-red-100 p-2">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <AlertDialogTitle className="text-xl mb-0">Payment Failed</AlertDialogTitle>
              </div>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-sm text-red-800 font-medium mb-1">{errorDetails.title}</p>
                  <p className="text-sm text-red-700">{errorDetails.message}</p>
                </div>
                
                {errorDetails.suggestion && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium mb-1">What to do next:</p>
                        <p className="text-sm text-amber-700">{errorDetails.suggestion}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {paymentError.code && (
                  <p className="text-xs text-gray-500">
                    Error code: {paymentError.code}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={handleCloseDialog} variant="outline" className="w-full">
              Try Again
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  notificationManager.showNotification('transaction-error', ToastComponent);
};

export const showCardOnFileTransactionNotification = ({ 
  transactionid, 
  amount, 
  status, 
  authcode, 
  cardLast4, 
  responsetext,
  additionalMessage, 
  onClose 
}: CardOnFileTransactionData) => {
  const ToastComponent: React.FC<{ onClose: () => void }> = ({ onClose: handleClose }) => {
    const [open, setOpen] = React.useState(true);

    React.useEffect(() => {
      return () => {
        document.body.style.overflow = '';
      };
    }, []);

    const handleCloseDialog = () => {
      setOpen(false);
      // Immediately reset body overflow to prevent blur issues
      document.body.style.overflow = '';
      setTimeout(() => {
        handleClose();
      }, 200);
    };

    const isApproved = status === 'approved';

    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => {
        if (!isOpen) {
          document.body.style.overflow = '';
        }
        setOpen(isOpen);
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={`rounded-full p-2 ${isApproved ? 'bg-green-100' : 'bg-red-100'}`}>
                {isApproved ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
              </div>
              <AlertDialogTitle className="text-xl mb-0">
                {isApproved ? 'Payment Successful!' : 'Payment Failed'}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-4">
                <div className={`rounded-lg p-4 ${isApproved ? 'bg-gray-50' : 'bg-red-50 border border-red-200'}`}>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Amount {isApproved ? 'Charged' : 'Attempted'}
                      </span>
                      <span className="font-semibold text-lg">{formatCurrency(amount)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <Hash className="h-4 w-4" />
                        Transaction ID
                      </span>
                      <span className="font-mono text-sm">{transactionid}</span>
                    </div>
                    
                    {authcode && isApproved && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Authorization Code</span>
                        <span className="font-mono text-sm">{authcode}</span>
                      </div>
                    )}
                    
                    {cardLast4 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Payment Method
                        </span>
                        <span className="text-sm">Card ending in {cardLast4}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {!isApproved && responsetext && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium mb-1">Reason:</p>
                        <p className="text-sm text-amber-700">{responsetext}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {additionalMessage && (
                  <p className={`text-sm p-3 rounded-lg border ${
                    isApproved 
                      ? 'text-gray-600 bg-blue-50 border-blue-200' 
                      : 'text-red-700 bg-red-50 border-red-200'
                  }`}>
                    {additionalMessage}
                  </p>
                )}
                
                {isApproved && (
                  <p className="text-sm text-gray-500">
                    A receipt has been sent to your email address.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={handleCloseDialog} variant={isApproved ? "default" : "outline"} className="w-full">
              {isApproved ? 'Done' : 'Close'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  notificationManager.showNotification('card-on-file-transaction', ToastComponent, onClose);
};

// Export the manager for cleanup purposes
export { notificationManager }; 
